import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { adminApiInstance, adminGet, adminPatch, adminPost } from "../../lib/adminApi";
import { Pencil } from "lucide-react";
import { PageHeader, StatusBadge, STATUS_LABEL, ErrorBanner, formatDate } from "../../components/admin/AdminUI";
import { Button, Card, Input, Label } from "../../components/fulkra";
import { TH, TD, RIJ } from "../../components/fulkra/tabel";
import { BESTELLING_STATUSES, ZOOLSOORT_LABELS } from "../../lib/constants";

/**
 * Bestellingen in de huisstijl.
 *
 * Restyling, geen herbouw: dezelfde filters, dezelfde statusovergangen met dezelfde
 * verplichte velden, dezelfde CSV-import met dezelfde uitleg, en alle data-testid's
 * ongewijzigd.
 */

const STATUSES = ["", ...BESTELLING_STATUSES];

// Waar een bestelling vanaf hier naartoe kan. De database bewaakt dit ook (migratie 045);
// deze lijst is er zodat het scherm niet iets aanbiedt wat toch geweigerd wordt.
const VERVOLGSTAPPEN = {
  klaar_om_te_bestellen: ["besteld", "foutief"],
  besteld: ["binnengekomen", "in_productie", "verzonden", "foutief"],
  in_productie: ["verzonden", "foutief"],
  binnengekomen: ["geleverd", "foutief"],
  verzonden: ["geleverd", "foutief"],
  geleverd: ["foutief"],
  foutief: [],
  herbesteld: [],
};

/** Nederlandse naam voor een stand; nooit de databasewaarde op het scherm (ADR-065). */
function standNaam(waarde) {
  return STATUS_LABEL[waarde] || waarde;
}

const KEUZEVELD = {
  minHeight: "var(--veld-hoogte-desktop)", borderRadius: "var(--radius)",
  border: "1px solid var(--border-subtle)", background: "var(--wit)",
  color: "var(--text-strong)", padding: "0 var(--ruimte-5)",
  fontSize: "var(--fs-body)", fontFamily: "var(--font-tekst)", boxShadow: "var(--schaduw-1)",
};


const MODAAL = {
  position: "fixed", inset: 0, zIndex: 40, display: "flex", alignItems: "center",
  justifyContent: "center", padding: "var(--ruimte-7)", background: "var(--overlay)",
  overflowY: "auto",
};

export default function BestellingenPage() {
  const [status, setStatus] = useState("");
  const [statusModal, setStatusModal] = useState(null);
  const [csvOpen, setCsvOpen] = useState(false);
  const qc = useQueryClient();

  // Gegroepeerd per werkgever, met de deelnemers eronder (Steven, 24-08-2026). Bewust
  // zonder bladeren: je bestelt per werkgever, en een lijst die halverwege een werkgever
  // afbreekt kun je niet als bestellijst gebruiken. Bij enkele honderden bestellingen moet
  // dit heroverwogen worden.
  const params = {};
  if (status) params.status = status;

  const q = useQuery({
    queryKey: ["bestellingen-per-werkgever", params],
    queryFn: () => adminGet("/api/admin/bestellingen/per-werkgever", params),
    keepPreviousData: true,
  });

  const statusMut = useMutation({
    mutationFn: ({ id, body }) => adminPatch(`/api/admin/bestellingen/${id}/status`, body),
    onSuccess: () => {
      toast.success("Status bijgewerkt");
      setStatusModal(null);
      qc.invalidateQueries({ queryKey: ["bestellingen-per-werkgever"] });
      qc.invalidateQueries({ queryKey: ["bestel-stapel"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Update mislukt"),
  });

  const groepen = q.data?.groepen || [];

  return (
    <div data-testid="bestellingen-page">
      <PageHeader
        title="Bestellingen"
        actions={
          <Button size="sm" onClick={() => setCsvOpen(true)} data-testid="btn-leverdatums-csv">
            Leverdatums CSV importeren
          </Button>
        }
      />

      <Stapel />

      <div style={{ marginBottom: "var(--ruimte-6)", display: "flex", flexWrap: "wrap", gap: "var(--ruimte-4)", alignItems: "center" }}>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label="Bestelling status-filter"
          data-testid="bestelling-status-filter"
          style={{ ...KEUZEVELD, width: 220 }}
        >
          {STATUSES.map((s) => <option key={s || "_all"} value={s}>{s ? standNaam(s) : "Alle"}</option>)}
        </select>
        {q.data ? (
          <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)" }} data-testid="bestellingen-totaal">
            {q.data.totaal_deelnemers} {q.data.totaal_deelnemers === 1 ? "deelnemer" : "deelnemers"} · {q.data.totaal_paar} paar
          </span>
        ) : null}
      </div>

      <ErrorBanner error={q.error} />

      {q.isLoading ? (
        <p style={{ color: "var(--text-soft)" }}>Laden…</p>
      ) : groepen.length === 0 ? (
        <p style={{ color: "var(--text-soft)" }} data-testid="bestellingen-leeg">
          Geen bestellingen gevonden.
        </p>
      ) : (
        <div style={{ display: "grid", gap: "var(--ruimte-6)" }}>
          {groepen.map((g) => (
            <WerkgeverGroep
              key={g.werkgever_id || g.bedrijfsnaam}
              groep={g}
              onStatus={(b) => setStatusModal(b)}
            />
          ))}
        </div>
      )}

      {statusModal ? (
        <StatusModal b={statusModal} onClose={() => setStatusModal(null)}
          onSubmit={(body) => statusMut.mutate({ id: statusModal.bestelling_id, body })}
          loading={statusMut.isPending} />
      ) : null}
      {csvOpen ? <LeverdatumsCsvModal onClose={() => setCsvOpen(false)} /> : null}
    </div>
  );
}

/**
 * Eén werkgever met zijn deelnemers eronder.
 *
 * Naam en adres staan erbij omdat Steven de prefab-dozen zelf uitdeelt: hij moet kunnen
 * zien wie wat krijgt en waar het heen gaat. Dit is het beheerscherm — de prefab-leverancier
 * krijgt nog steeds alleen maten en aantallen.
 */
function WerkgeverGroep({ groep, onStatus }) {
  const testid = `werkgever-groep-${groep.werkgever_id || "zonder"}`;
  return (
    <div
      data-testid={testid}
      style={{
        background: "var(--wit)", border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-vlak)", boxShadow: "var(--elevation-card)", overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex", flexWrap: "wrap", alignItems: "baseline", gap: "var(--ruimte-4)",
          padding: "var(--ruimte-5)", borderBottom: "1px solid var(--border-subtle)",
          background: "var(--surface-sunken)",
        }}
      >
        <span style={{ font: "var(--text-h3)", color: "var(--text-strong)" }}>{groep.bedrijfsnaam}</span>
        <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>
          {groep.aantal_deelnemers} {groep.aantal_deelnemers === 1 ? "deelnemer" : "deelnemers"} · {groep.aantal_paar} paar
        </span>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--fs-body)" }}>
          <thead style={{ background: "var(--wit)" }}>
            <tr>
              {["Klantnr.", "Naam", "Adres", "Maat", "Soort", "Paar", "Status"].map((k) => (
                <th key={k} scope="col" style={TH}>{k}</th>
              ))}
              <th scope="col" style={{ ...TH, textAlign: "right" }}>Actie</th>
            </tr>
          </thead>
          <tbody>
            {groep.regels.map((r) => (
              <tr key={r.bestelling_id} data-testid={`bestelling-row-${r.bestelling_id}`} style={RIJ}>
                <td style={{ ...TD, fontFamily: "var(--font-mono)", fontSize: "var(--fs-sm)" }}>{r.klantnummer || "—"}</td>
                <td style={{ ...TD, color: "var(--text-strong)" }}>{r.naam || "—"}</td>
                <td style={{ ...TD, fontSize: "var(--fs-sm)" }}>
                  {r.adres || r.postcode_plaats ? (
                    <>
                      <div>{r.adres || ""}</div>
                      <div style={{ color: "var(--text-soft)" }}>{r.postcode_plaats || ""}</div>
                    </>
                  ) : (
                    <span style={{ color: "var(--text-soft)" }}>Adres ontbreekt</span>
                  )}
                </td>
                <td style={{ ...TD, fontVariantNumeric: "tabular-nums" }}>{maatTekst(r.maatschoen_eu)}</td>
                <td style={{ ...TD, fontSize: "var(--fs-sm)" }}>{ZOOLSOORT_LABELS[r.zoolsoort] || "—"}</td>
                <td style={{ ...TD, fontVariantNumeric: "tabular-nums" }}>{r.aantal_paar}</td>
                <td style={TD}><StatusBadge value={r.status} testid={`bestel-status-${r.bestelling_id}`} /></td>
                <td style={{ ...TD, textAlign: "right" }}>
                  <Button variant="link" onClick={() => onStatus(r)} data-testid={`btn-bestel-status-${r.bestelling_id}`}>
                    Status wijzigen
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** 41 blijft "41", 41,5 blijft "41,5" — een punt is Engels en hoort niet op dit scherm. */
function maatTekst(maat) {
  if (maat === null || maat === undefined) return "—";
  const getal = Number(maat);
  if (!Number.isFinite(getal)) return "—";
  return Number.isInteger(getal) ? String(getal) : String(getal).replace(".", ",");
}

/**
 * Wat er klaarligt om besteld te worden.
 *
 * Besluit 23-08-2026: na het aanmeten komt een bestelling eerst op deze stapel, en Steven
 * bestelt alles wat openstaat in één keer. De twee soorten gaan naar verschillende
 * leveranciers en in een totaal verschillende vorm — vandaar twee aparte vakken:
 *
 *   Prefab  — de leverancier krijgt alléén een telling per schoenmaat. Die tekst staat
 *             hier kant-en-klaar om te kopiëren; namen en adressen gaan niet mee, want de
 *             doos komt bij ons binnen en wij delen hem uit.
 *   Custom  — PLT krijgt een bestand met één regel per persoon, mét scan en afleveradres.
 */
function Stapel() {
  const qc = useQueryClient();
  const [bezig, setBezig] = useState(null);

  const q = useQuery({
    queryKey: ["bestel-stapel"],
    queryFn: () => adminGet("/api/admin/bestellingen/stapel"),
  });

  const bestelMut = useMutation({
    mutationFn: (zoolsoort) => adminPost("/api/admin/bestellingen/stapel/markeer-besteld", { zoolsoort }),
    onSuccess: (data) => {
      const aantal = data?.aantal_besteld ?? 0;
      toast.success(`${aantal} ${aantal === 1 ? "bestelling" : "bestellingen"} op besteld gezet`);
      if (data?.mislukt?.length) {
        toast.error(`${data.mislukt.length} lukte(n) niet — zie de lijst hieronder`);
      }
      qc.invalidateQueries({ queryKey: ["bestel-stapel"] });
      qc.invalidateQueries({ queryKey: ["bestellingen"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Bestellen mislukt"),
    onSettled: () => setBezig(null),
  });

  async function downloadCustom() {
    try {
      const api = adminApiInstance();
      const res = await api.get("/api/admin/bestellingen/csv-export", {
        params: { zoolsoort: "custom", status: "klaar_om_te_bestellen" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "bestellingen_custom.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Downloaden mislukt");
    }
  }

  const prefab = q.data?.prefab;
  const custom = q.data?.custom;
  const totaal = (prefab?.aantal || 0) + (custom?.aantal || 0);
  const totaalParen = (prefab?.paren || 0) + (custom?.paren || 0);

  if (q.isLoading) return null;

  return (
    <div style={{ marginBottom: "var(--ruimte-7)" }} data-testid="bestel-stapel">
      <Card title={`Klaar om te bestellen — ${totaal} ${totaal === 1 ? "deelnemer" : "deelnemers"}, ${totaalParen} paar`}>
        {totaal === 0 ? (
          <p style={{ margin: 0, color: "var(--text-soft)" }} data-testid="stapel-leeg">
            Er ligt niets klaar. Zodra iemand is aangemeten, verschijnt de bestelling hier.
          </p>
        ) : (
          <div style={{ display: "grid", gap: "var(--ruimte-6)", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" }}>
            <StapelVak
              titel="Prefab"
              uitleg="De leverancier krijgt alleen de maten en de aantallen. De doos komt bij ons binnen."
              aantal={prefab?.aantal || 0}
              paren={prefab?.paren || 0}
              testid="stapel-prefab"
              bezig={bezig === "prefab"}
              onBestel={() => { setBezig("prefab"); bestelMut.mutate("prefab"); }}
            >
              {prefab?.aantal ? (
                <>
                  <table style={{ borderCollapse: "collapse", fontSize: "var(--fs-sm)", marginBottom: "var(--ruimte-4)" }}>
                    <tbody>
                      {prefab.maten.map((m) => (
                        <MaatRegel key={m.maat} m={m} />
                      ))}
                    </tbody>
                  </table>
                  <Button
                    size="sm" variant="secundair"
                    onClick={() => {
                      navigator.clipboard?.writeText(prefab.besteltekst);
                      toast.success("Bestelregels gekopieerd");
                    }}
                    data-testid="stapel-prefab-kopieer"
                  >
                    Bestelregels kopiëren
                  </Button>
                  {prefab.mail ? (
                    <Bestelmail mail={prefab.mail} bijzonderheden={prefab.bijzonderheden || []} />
                  ) : null}
                </>
              ) : null}
            </StapelVak>

            <StapelVak
              titel="Custom (PLT)"
              uitleg="PLT krijgt een bestand met scan en afleveradres, en stuurt rechtstreeks naar de deelnemer."
              aantal={custom?.aantal || 0}
              paren={custom?.paren || 0}
              testid="stapel-custom"
              bezig={bezig === "custom"}
              onBestel={() => { setBezig("custom"); bestelMut.mutate("custom"); }}
            >
              {custom?.aantal ? (
                <Button size="sm" variant="secundair" onClick={downloadCustom} data-testid="stapel-custom-download">
                  Bestand downloaden
                </Button>
              ) : null}
            </StapelVak>
          </div>
        )}
      </Card>
    </div>
  );
}


/**
 * De bestelmail voor de prefab-leverancier, kant en klaar.
 *
 * De app verstuurt hem niet. Afspraak met Steven (23-08-2026): eerst moet de rest van de
 * pijplijn kloppen, dan pas gaat er post uit. U kopieert de tekst of opent hem in uw eigen
 * mailprogramma, leest hem na, en verstuurt zelf.
 *
 * Wat er ontbreekt staat er met zoveel woorden bij. Een verzonnen afleveradres in een
 * bestelling van een paar honderd euro is een duurdere fout dan een lege regel.
 */
function Bestelmail({ mail, bijzonderheden = [] }) {
  const [open, setOpen] = useState(false);
  // Welke opmerkingen meegaan. Leeg is de standaard: een opmerking komt alleen bij de
  // leverancier terecht als iemand daar bewust ja tegen zegt (Steven, 24-08-2026: "dit moet
  // voor nu nog niet automatisch gebeuren").
  const [gekozen, setGekozen] = useState([]);
  const [metOpmerkingen, setMetOpmerkingen] = useState(null);
  const [bezig, setBezig] = useState(false);
  // Zelf aangepast, ter plekke (Steven, 31-08-2026: "ik wil hier gewoon een pas aan teken
  // hebben en in de mail zelf gelijk kan aanpassen"). Blijft staan tot "Begin opnieuw":
  // vinkt u daarna nog een opmerking aan, dan overschrijft de server uw tekst niet.
  const [bewerken, setBewerken] = useState(false);
  const [bewerkt, setBewerkt] = useState(null);
  const [verstuurd, setVerstuurd] = useState(null);
  const [verzendt, setVerzendt] = useState(false);

  // De getoonde mail: de kale versie, of — zodra er iets is aangevinkt — de versie die de
  // server met die opmerkingen erin heeft opgesteld. De tekst wordt op één plek geschreven.
  const basis = metOpmerkingen || mail;
  const getoond = bewerkt ? { ...basis, ...bewerkt } : basis;

  function pasAan(veld, waarde) {
    setBewerkt({
      onderwerp: getoond.onderwerp,
      tekst: getoond.tekst,
      [veld]: waarde,
    });
  }

  function beginOpnieuw() {
    setBewerkt(null);
    setBewerken(false);
  }

  async function verstuur() {
    setVerzendt(true);
    try {
      const antwoord = await adminPost("/api/admin/bestellingen/stapel/bestelmail/versturen", {
        zoolsoort: "prefab",
        onderwerp: getoond.onderwerp,
        tekst: getoond.tekst,
      });
      setVerstuurd(antwoord);
      setBewerken(false);
      toast.success("De bestelmail is verstuurd");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "De mail kon niet verstuurd worden");
    } finally {
      setVerzendt(false);
    }
  }
  const compleet = !getoond.ontbreekt?.length;

  async function wisselOpmerking(id) {
    const nieuw = gekozen.includes(id) ? gekozen.filter((x) => x !== id) : [...gekozen, id];
    setGekozen(nieuw);
    if (nieuw.length === 0) {
      setMetOpmerkingen(null);
      return;
    }
    setBezig(true);
    try {
      const antwoord = await adminPost("/api/admin/bestellingen/stapel/bestelmail", {
        zoolsoort: "prefab",
        bestelling_ids: nieuw,
      });
      setMetOpmerkingen(antwoord);
    } catch (e) {
      toast.error("De mail kon niet worden bijgewerkt");
      setGekozen(gekozen);
    } finally {
      setBezig(false);
    }
  }

  function kopieer() {
    navigator.clipboard?.writeText(getoond.tekst);
    toast.success("Mailtekst gekopieerd");
  }

  const mailtoLink =
    `mailto:${encodeURIComponent(getoond.aan || "")}` +
    `?subject=${encodeURIComponent(getoond.onderwerp)}` +
    `&body=${encodeURIComponent(getoond.tekst)}`;

  return (
    <div style={{ marginTop: "var(--ruimte-5)" }} data-testid="bestelmail">
      <Button size="sm" variant="secundair" onClick={() => setOpen(!open)} data-testid="bestelmail-openen">
        {open ? "Bestelmail verbergen" : "Bestelmail bekijken"}
      </Button>

      {open ? (
        <div
          style={{
            marginTop: "var(--ruimte-4)", border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius)", background: "var(--wit)", padding: "var(--ruimte-5)",
            display: "grid", gap: "var(--ruimte-4)",
          }}
        >
          {!compleet ? (
            <div
              data-testid="bestelmail-ontbreekt"
              style={{
                border: "1px solid var(--aandacht-rand)", background: "var(--aandacht-zacht)",
                borderRadius: "var(--radius)", padding: "var(--ruimte-4)",
                fontSize: "var(--fs-sm)", color: "var(--text-body)",
              }}
            >
              Vul dit eerst aan bij Instellingen, anders is de mail niet af:{" "}
              {getoond.ontbreekt.join(", ")}.
            </div>
          ) : null}

          <div style={{ display: "flex", alignItems: "flex-start", gap: "var(--ruimte-4)" }}>
            <div style={{ fontSize: "var(--fs-sm)", flex: 1, minWidth: 0 }}>
              <div><span style={{ color: "var(--text-soft)" }}>Aan: </span>
                {getoond.aan || <em style={{ color: "var(--text-soft)" }}>nog niet ingevuld</em>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--ruimte-3)" }}>
                <span style={{ color: "var(--text-soft)" }}>Onderwerp: </span>
                {bewerken ? (
                  <input
                    value={getoond.onderwerp}
                    onChange={(e) => pasAan("onderwerp", e.target.value)}
                    data-testid="bestelmail-onderwerp-veld"
                    aria-label="Onderwerp"
                    style={{
                      flex: 1, minWidth: 0, font: "inherit", padding: "2px var(--ruimte-3)",
                      border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)",
                      background: "var(--wit)", color: "var(--text-body)",
                    }}
                  />
                ) : (
                  getoond.onderwerp
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setBewerken(!bewerken)}
              aria-expanded={bewerken}
              aria-label={bewerken ? "Klaar met aanpassen" : "Mail aanpassen"}
              title={bewerken ? "Klaar met aanpassen" : "Mail aanpassen"}
              data-testid="bestelmail-bewerken"
              style={{
                display: "flex", alignItems: "center", gap: "var(--ruimte-2)",
                background: bewerken ? "var(--surface-sunken)" : "none",
                border: "1px solid " + (bewerken ? "var(--border-subtle)" : "transparent"),
                borderRadius: "var(--radius)", padding: "var(--ruimte-2) var(--ruimte-3)",
                cursor: "pointer", color: "var(--text-action)",
                fontSize: "var(--fs-sm)", fontFamily: "var(--font-tekst)",
              }}
            >
              <Pencil size={16} aria-hidden="true" />
              {bewerken ? "Klaar" : "Aanpassen"}
            </button>
          </div>

          {bijzonderheden.length > 0 ? (
            <div
              data-testid="bestelmail-opmerkingen"
              style={{
                border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)",
                padding: "var(--ruimte-4)", display: "grid", gap: "var(--ruimte-3)",
              }}
            >
              <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-strong)", fontWeight: "var(--fw-medium)" }}>
                Opmerkingen meesturen?
              </span>
              <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
                Vink per bestelling aan wat de leverancier moet weten. Niets aangevinkt betekent
                dat er geen opmerkingen meegaan.
              </span>
              {bijzonderheden.map((b) => (
                <label
                  key={b.bestelling_id}
                  style={{ display: "flex", gap: "var(--ruimte-3)", alignItems: "flex-start", fontSize: "var(--fs-sm)" }}
                >
                  <input
                    type="checkbox"
                    checked={gekozen.includes(b.bestelling_id)}
                    onChange={() => wisselOpmerking(b.bestelling_id)}
                    disabled={bezig}
                    data-testid={`opmerking-${b.bestelling_id}`}
                    style={{ marginTop: 3 }}
                  />
                  <span>
                    <strong style={{ color: "var(--text-strong)" }}>maat {b.maat}</strong>
                    {" — "}{b.opmerking}
                  </span>
                </label>
              ))}
            </div>
          ) : null}

          {bewerken ? (
            <textarea
              value={getoond.tekst}
              onChange={(e) => pasAan("tekst", e.target.value)}
              data-testid="bestelmail-tekst-veld"
              aria-label="Mailtekst"
              rows={Math.min(30, (getoond.tekst || "").split(String.fromCharCode(10)).length + 2)}
              style={{
                margin: 0, padding: "var(--ruimte-4)", background: "var(--wit)",
                border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)",
                fontFamily: "var(--font-tekst)", fontSize: "var(--fs-sm)",
                color: "var(--text-body)", width: "100%", resize: "vertical",
              }}
            />
          ) : (
            <pre
              data-testid="bestelmail-tekst"
              style={{
                margin: 0, padding: "var(--ruimte-4)", background: "var(--surface-sunken)",
                borderRadius: "var(--radius)", fontFamily: "var(--font-tekst)",
                fontSize: "var(--fs-sm)", whiteSpace: "pre-wrap", color: "var(--text-body)",
              }}
            >
              {getoond.tekst}
            </pre>
          )}

          {bewerkt ? (
            <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-soft)" }} data-testid="bestelmail-aangepast">
              U heeft deze mail zelf aangepast. Dat geldt alleen voor deze verzending — de
              volgende bestelling begint weer met de standaardtekst.
            </p>
          ) : null}

          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--ruimte-4)" }}>
            <Button
              size="sm" onClick={verstuur}
              disabled={verzendt || !!verstuurd || bezig}
              data-testid="bestelmail-versturen"
            >
              {verzendt ? "Bezig met versturen…" : verstuurd ? "Verstuurd" : "Versturen"}
            </Button>
            <Button size="sm" variant="secundair" onClick={kopieer} data-testid="bestelmail-kopieer">
              Mailtekst kopiëren
            </Button>
            <Button
              size="sm" variant="secundair" as="a" href={mailtoLink}
              data-testid="bestelmail-openen-in-mail"
            >
              Openen in mailprogramma
            </Button>
            {bewerkt ? (
              <Button size="sm" variant="secundair" onClick={beginOpnieuw} data-testid="bestelmail-opnieuw">
                Begin opnieuw
              </Button>
            ) : null}
          </div>

          {verstuurd ? (
            <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-strong)" }} data-testid="bestelmail-verstuurd">
              Verstuurd naar {verstuurd.aan} op {formatDate(verstuurd.verzonden_op)}.
            </p>
          ) : (
            <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-soft)" }}>
              De app verstuurt hem naar het leveranciersadres uit Instellingen. Wilt u hem liever
              zelf versturen, gebruik dan Kopiëren of Openen in mailprogramma.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Eén maat in de prefab-telling, met de deelnemers erachter uitklapbaar.
 *
 * Steven, 31-08-2026: "ik wil graag bij de zolen per maat een uitklap menu hebben welke
 * personen die zolen dan krijgen, dit hoeft niet in de mail meegezonden te worden maar
 * vind ik wel fijn hier zo te kunnen zien dan." Bij het uitdelen moet je weten wie welke
 * maat krijgt; de leverancier blijft alleen maten en aantallen zien.
 *
 * Dicht bij het openen, want de telling is waar je op deze plek naar kijkt.
 */
function MaatRegel({ m }) {
  const [open, setOpen] = useState(false);
  const deelnemers = m.deelnemers || [];
  const label = `maat ${m.maat}`;
  return (
    <>
      <tr data-testid={`stapel-maat-${m.maat}`}>
        <td style={{ padding: "2px var(--ruimte-4) 2px 0", fontVariantNumeric: "tabular-nums" }}>
          {deelnemers.length ? (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              aria-expanded={open}
              data-testid={`stapel-maat-${m.maat}-toon`}
              style={{
                display: "flex", alignItems: "center", gap: "var(--ruimte-2)",
                background: "none", border: "none", padding: 0, cursor: "pointer",
                font: "inherit", fontVariantNumeric: "tabular-nums",
                color: "var(--text-action)", fontFamily: "var(--font-tekst)",
              }}
            >
              <span className="uitklap-pijl" aria-hidden="true" style={{ transform: open ? "rotate(90deg)" : "none" }}>
                &#9656;
              </span>
              {label}
            </button>
          ) : (
            label
          )}
        </td>
        <td style={{ padding: "2px 0", fontVariantNumeric: "tabular-nums", color: "var(--text-strong)" }}>
          {m.paren} paar
        </td>
      </tr>
      {open ? (
        <tr data-testid={`stapel-maat-${m.maat}-personen`}>
          <td colSpan={2} style={{ padding: "0 0 var(--ruimte-3) var(--ruimte-5)" }}>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, color: "var(--text-soft)" }}>
              {deelnemers.map((d, i) => (
                <li key={d.werknemer_id || `${m.maat}-${i}`} style={{ padding: "1px 0" }}>
                  <span style={{ color: "var(--text-strong)" }}>{d.naam || "Naam onbekend"}</span>
                  {d.klantnummer ? ` · ${d.klantnummer}` : ""}
                  {d.paren > 1 ? ` · ${d.paren} paar` : ""}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function StapelVak({ titel, uitleg, aantal, paren, testid, children, onBestel, bezig }) {
  return (
    <div
      data-testid={testid}
      style={{
        border: "1px solid var(--border-subtle)", borderRadius: "var(--radius)",
        padding: "var(--ruimte-5)", background: "var(--surface-sunken)",
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: "var(--ruimte-3)" }}>
        <span style={{ font: "var(--text-h3)", color: "var(--text-strong)" }}>{titel}</span>
        <span style={{ fontSize: "var(--fs-sm)", color: "var(--text-soft)" }} data-testid={`${testid}-aantal`}>
          {aantal} {aantal === 1 ? "deelnemer" : "deelnemers"}
          {paren ? ` · ${paren} paar` : ""}
        </span>
      </div>
      <p style={{ margin: "var(--ruimte-3) 0 var(--ruimte-5)", fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>
        {uitleg}
      </p>
      {children}
      {aantal > 0 ? (
        <div style={{ marginTop: "var(--ruimte-5)" }}>
          <Button onClick={onBestel} disabled={bezig} data-testid={`${testid}-besteld`}>
            {bezig ? "Bezig…" : "Markeer als besteld"}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function StatusModal({ b, onClose, onSubmit, loading }) {
  const opties = VERVOLGSTAPPEN[b.status] || [];
  const [nieuweStatus, setNieuweStatus] = useState(opties[0] || b.status);
  const [track, setTrack] = useState(b.track_trace_link || "");
  const [leverdatum, setLeverdatum] = useState(b.daadwerkelijke_leverdatum ? b.daadwerkelijke_leverdatum.slice(0, 10) : "");
  const verzondenVereist = nieuweStatus === "verzonden";
  const geleverdVereist = nieuweStatus === "geleverd";
  const valid = opties.length > 0
    && (!verzondenVereist || track.trim().length > 0)
    && (!geleverdVereist || leverdatum.length > 0);
  return (
    <div role="dialog" aria-modal="true" aria-label={`Bestelling ${b.klantnummer}`} className="fulkra" style={MODAAL} data-testid="modal-bestel-status">
      <div style={{ width: "100%", maxWidth: 440 }}>
        <Card title={`Bestelling ${b.klantnummer}`}>
          <div style={{ display: "grid", gap: "var(--ruimte-6)" }}>
            <p style={{ margin: 0, fontSize: "var(--fs-sm)", color: "var(--text-soft)" }}>
              Staat nu op <strong style={{ color: "var(--text-strong)" }}>{standNaam(b.status)}</strong>
              {b.zoolsoort ? ` · ${ZOOLSOORT_LABELS[b.zoolsoort] || b.zoolsoort}` : ""}
            </p>
            {opties.length === 0 ? (
              <p style={{ margin: 0, color: "var(--text-body)" }} data-testid="bestel-geen-vervolg">
                Deze bestelling is afgesloten; er is geen volgende stap meer.
              </p>
            ) : null}
            <div style={{ display: "grid", gap: "var(--ruimte-3)" }}>
              <Label htmlFor="bestel-status">Volgende stap</Label>
              <select
                id="bestel-status"
                value={nieuweStatus}
                onChange={(e) => setNieuweStatus(e.target.value)}
                aria-label="Bestelling status select"
                data-testid="modal-bestel-status-select"
                style={{ ...KEUZEVELD, width: "100%" }}
              >
                {opties.map((s) => <option key={s} value={s}>{standNaam(s)}</option>)}
              </select>
            </div>

            <div style={{ display: "grid", gap: "var(--ruimte-3)" }}>
              <Label htmlFor="bestel-track" required={verzondenVereist}>Track &amp; trace link</Label>
              <Input
                id="bestel-track"
                value={track}
                onChange={(e) => setTrack(e.target.value)}
                data-testid="modal-bestel-tracktrace"
                placeholder="https://..."
                mobiel={false}
                invalid={verzondenVereist && track.trim().length === 0}
              />
            </div>

            <div style={{ display: "grid", gap: "var(--ruimte-3)" }}>
              <Label htmlFor="bestel-leverdatum" required={geleverdVereist}>Daadwerkelijke leverdatum</Label>
              <Input
                id="bestel-leverdatum"
                type="date"
                value={leverdatum}
                onChange={(e) => setLeverdatum(e.target.value)}
                data-testid="modal-bestel-leverdatum"
                mobiel={false}
                invalid={geleverdVereist && leverdatum.length === 0}
              />
            </div>
          </div>

          <div style={{ marginTop: "var(--ruimte-7)", display: "flex", justifyContent: "flex-end", gap: "var(--ruimte-4)" }}>
            <Button variant="secundair" onClick={onClose} data-testid="modal-bestel-cancel">Annuleren</Button>
            <Button
              disabled={loading || !valid}
              onClick={() => onSubmit({ nieuwe_status: nieuweStatus, track_trace_link: track || null, daadwerkelijke_leverdatum: leverdatum || null })}
              data-testid="modal-bestel-submit"
            >
              {loading ? "Bezig…" : "Opslaan"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}

function LeverdatumsCsvModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  async function upload() {
    if (!file) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const api = adminApiInstance();
      const res = await api.post("/api/admin/bestellingen/leverdatums/csv-import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
    } catch (e) {
      setError(e);
    } finally { setBusy(false); }
  }

  return (
    <div role="dialog" aria-modal="true" aria-label="Bestellingen-CSV importeren" className="fulkra" style={MODAAL} data-testid="modal-leverdatums-csv">
      <div style={{ width: "100%", maxWidth: 720, margin: "auto" }}>
        <Card title="Bestellingen-CSV importeren">
          <div
            data-testid="csv-instructies"
            style={{
              borderRadius: "var(--radius)", border: "1px solid var(--actie-rand)",
              background: "var(--actie-zacht)", padding: "var(--ruimte-5)",
              fontSize: "var(--fs-sm)", color: "var(--text-body)",
            }}
          >
            <p style={{ fontWeight: "var(--fw-medium)", color: "var(--text-strong)" }}>Verplichte kolommen (5):</p>
            <code style={{ display: "block", marginTop: "var(--ruimte-3)", fontFamily: "var(--font-mono)", wordBreak: "break-all" }}>
              bestelreferentie,status,datum,track_trace_link,fout_reden
            </code>
            <ul style={{ marginTop: "var(--ruimte-4)", paddingLeft: "var(--ruimte-7)", display: "grid", gap: 2 }}>
              <li><code>bestelreferentie</code> — UUID van de bestelling</li>
              <li><code>status</code> — één van: <code>verzonden</code>, <code>geleverd</code>, <code>foutief</code></li>
              <li><code>datum</code> — ISO-datum <code>YYYY-MM-DD</code> (verwachte leverdatum bij <code>verzonden</code>, daadwerkelijke bij <code>geleverd</code>)</li>
              <li><code>track_trace_link</code> — verplicht bij status=<code>verzonden</code></li>
              <li><code>fout_reden</code> — verplicht bij status=<code>foutief</code></li>
            </ul>
            <p style={{ marginTop: "var(--ruimte-4)" }}>Max 500 rijen / 1 MB. Partial-success: foutieve rijen blokkeren de rest niet.</p>
          </div>

          <input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            data-testid="leverdatums-csv-file"
            style={{ marginTop: "var(--ruimte-5)", fontSize: "var(--fs-body)", color: "var(--text-body)" }}
          />

          <ErrorBanner error={error} />

          {result ? (
            <div style={{ marginTop: "var(--ruimte-5)", display: "grid", gap: "var(--ruimte-4)" }} data-testid="leverdatums-csv-result">
              <div
                style={{
                  borderRadius: "var(--radius)", border: "1px solid var(--positief-rand)",
                  background: "var(--positief-zacht)", padding: "var(--ruimte-5)",
                }}
              >
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--ruimte-4)", color: "var(--text-positief)" }}>
                  {[
                    ["Aangeleverd", result.aantal_aangeleverd ?? 0, "csv-result-aangeleverd"],
                    ["Succesvol", result.aantal_succesvol ?? 0, "csv-result-succesvol"],
                    ["Fouten", result.aantal_fouten ?? 0, "csv-result-fouten"],
                  ].map(([label, waarde, testid]) => (
                    <div key={testid}>
                      <span style={{ fontSize: "var(--fs-xs)", letterSpacing: "var(--ls-label)", textTransform: "uppercase" }}>{label}</span>
                      <div style={{ font: "var(--text-h3)" }} data-testid={testid}>{waarde}</div>
                    </div>
                  ))}
                </div>
                {result.file_hash_sha256 ? (
                  <p style={{ marginTop: "var(--ruimte-4)", fontFamily: "var(--font-mono)", fontSize: "var(--fs-xs)", wordBreak: "break-all", color: "var(--text-positief)" }}>
                    sha256: {result.file_hash_sha256}
                  </p>
                ) : null}
              </div>

              {result.fouten?.length ? (
                <div
                  data-testid="csv-result-fouten-tabel"
                  style={{
                    maxHeight: 200, overflow: "auto", borderRadius: "var(--radius)",
                    border: "1px solid var(--alarm-rand)", background: "var(--alarm-zacht)",
                    padding: "var(--ruimte-4)", fontSize: "var(--fs-sm)",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {["Rij", "Referentie", "Code", "Bericht"].map((k) => (
                          <th key={k} scope="col" style={{ textAlign: "left", padding: "2px var(--ruimte-3)", fontSize: "var(--fs-xs)", letterSpacing: "var(--ls-label)", textTransform: "uppercase", color: "var(--text-alarm)" }}>{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.fouten.map((f, i) => (
                        <tr key={i} data-testid={`csv-fout-${i}`}>
                          <td style={{ padding: "2px var(--ruimte-3)", fontFamily: "var(--font-mono)" }}>{f.rij_nummer}</td>
                          <td style={{ padding: "2px var(--ruimte-3)", fontFamily: "var(--font-mono)", wordBreak: "break-all" }}>{f.bestelreferentie || "—"}</td>
                          <td style={{ padding: "2px var(--ruimte-3)", fontFamily: "var(--font-mono)" }}>{f.error_code}</td>
                          <td style={{ padding: "2px var(--ruimte-3)" }}>{f.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}

              {result.successes?.length ? (
                <details style={{ fontSize: "var(--fs-sm)" }} data-testid="csv-result-successes">
                  <summary style={{ cursor: "pointer", color: "var(--text-body)" }}>
                    Toon {result.successes.length} succesvolle rijen
                  </summary>
                  <ul style={{ marginTop: "var(--ruimte-3)", paddingLeft: "var(--ruimte-7)" }}>
                    {result.successes.slice(0, 50).map((s, i) => (
                      <li key={i} style={{ fontFamily: "var(--font-mono)" }}>
                        rij {s.rij_nummer}: {s.bestelling_id?.slice(0, 8)} → {s.nieuwe_status}
                      </li>
                    ))}
                  </ul>
                </details>
              ) : null}
            </div>
          ) : null}

          <div style={{ marginTop: "var(--ruimte-7)", display: "flex", justifyContent: "flex-end", gap: "var(--ruimte-4)" }}>
            <Button variant="secundair" onClick={onClose} data-testid="leverdatums-csv-cancel">Sluiten</Button>
            <Button onClick={upload} disabled={!file || busy} data-testid="leverdatums-csv-submit">
              {busy ? "Uploaden…" : "Upload"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
